import { useState } from 'react';
import { MessageCircle, X, Phone } from 'lucide-react';

/**
 * Floating WhatsApp Support Widget.
 * Replaces the previous in-app LiveChatWidget so all support flows go through WhatsApp.
 */
const WHATSAPP_NUMBER = '905531380003'; // +90 553 138 00 03 (E.164 without leading +)
const DISPLAY_NUMBER = '0553 138 00 03';
const DEFAULT_MESSAGE =
  'Merhaba, tesiskirala.com üzerinden yazıyorum. Destek almak istediğim bir konu var, yardımcı olabilir misiniz?';

const buildWhatsAppUrl = (message = DEFAULT_MESSAGE) => {
  const encoded = encodeURIComponent(message);
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encoded}`;
};

const WhatsAppSupportWidget = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* Popover card */}
      {open && (
        <div
          data-testid="whatsapp-support-card"
          className="fixed bottom-24 right-4 sm:right-6 z-50 w-[92vw] max-w-sm bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in slide-in-from-bottom-4"
        >
          {/* Header */}
          <div className="bg-[#25D366] text-white px-5 py-4 flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center">
                <MessageCircle className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-base leading-tight">TesisKirala Destek</h3>
                <p className="text-xs text-white/90 mt-0.5">
                  <span className="inline-block w-2 h-2 rounded-full bg-emerald-300 mr-1 align-middle"></span>
                  Genellikle birkaç dakika içinde yanıt veriyoruz
                </p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/80 hover:text-white transition"
              aria-label="Kapat"
              data-testid="whatsapp-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-5 space-y-4 bg-slate-50">
            <div className="bg-white rounded-xl px-4 py-3 shadow-sm border border-slate-100">
              <p className="text-sm text-slate-700 leading-relaxed">
                Merhaba! 👋 Rezervasyon, ödeme veya tesis işletmeci başvurusu hakkında sorularınız
                için WhatsApp üzerinden bize yazabilirsiniz. Ekibimiz kısa sürede size dönüş yapar.
              </p>
            </div>
            <div className="bg-white rounded-xl px-4 py-3 shadow-sm border border-slate-100 flex items-center gap-3">
              <Phone className="w-5 h-5 text-emerald-600 shrink-0" />
              <div>
                <p className="text-xs text-slate-500">Destek Hattı</p>
                <a
                  href={`tel:+${WHATSAPP_NUMBER}`}
                  className="text-sm font-semibold text-slate-900 hover:text-emerald-600"
                  data-testid="whatsapp-tel-link"
                >
                  {DISPLAY_NUMBER}
                </a>
              </div>
            </div>
            <a
              href={buildWhatsAppUrl()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex items-center justify-center gap-2 w-full bg-[#25D366] hover:bg-[#20BA5A] text-white font-semibold py-3 rounded-xl transition shadow-md hover:shadow-lg"
              data-testid="whatsapp-open-chat-btn"
            >
              <MessageCircle className="w-5 h-5" />
              WhatsApp&apos;ta Sohbet Başlat
            </a>
            <p className="text-[11px] text-slate-500 text-center leading-relaxed">
              Butona tıkladığınızda WhatsApp uygulaması hazır mesaj ile açılır.
              Çalışma saatleri: Hafta içi 09:00 - 21:00, Hafta sonu 10:00 - 19:00
            </p>
          </div>
        </div>
      )}

      {/* Floating trigger button */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Destek panelini kapat' : 'WhatsApp destek panelini aç'}
        data-testid="whatsapp-support-trigger"
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#20BA5A] text-white shadow-xl hover:shadow-2xl flex items-center justify-center transition-all hover:scale-105 active:scale-95"
      >
        {open ? (
          <X className="w-6 h-6" />
        ) : (
          <>
            <svg viewBox="0 0 24 24" className="w-7 h-7" fill="currentColor" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            <span className="absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full animate-pulse border-2 border-white"></span>
          </>
        )}
      </button>
    </>
  );
};

export default WhatsAppSupportWidget;
